import { createErrorComponent } from "components/Errors/ErrorComponent";
import SideBar from "components/SideBar/SideBar";
import TanguayHeader from "components/TanguayHeader/TanguayHeader";
import WarehouseSelectionHeader from "components/WarehouseElements/WarehouseHeaders/WarhouseSelectionButton/WarehouseSelectionCombo";
import { UNAUTHORIZED_ERROR_CODE } from "constants/error/ErrorCodes";
import { INVALID_INDEX } from "constants/globals";
import { LEGEND_MODE, PARAM_KEY, VIEW_MODE } from "constants/navigation";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import { useLocations } from "hooks/useLocations";
import { useResetTokenValidity } from "hooks/useResetTokenValidityHook";
import { WarehouseInfos } from "models/PlanInfos";
import { SnackbarProvider } from "notistack";
import { JSX, useEffect, useState } from "react";
import { Outlet, useSearchParams } from "react-router";
import { selectLocations } from "store/Selectors/locationsSelector";
import {
  addLoadingRequest,
  logout,
  removeLoadingRequest,
} from "store/Slices/appStateSlice";
import { resetGridEvent } from "store/Slices/gridEventSlice";
import { Location } from "store/Slices/locationSlice";
import { StatusResource } from "store/Slices/Status/statusSlice";
import { addNewURLQueryParams } from "utils/getNewURLQueryParams";
import { getUrlParamOrLocalStorage } from "utils/getUrlParamOrLocalStorage";
import { convertToEnumValue } from "utils/toEnumValue";

import { EntrepotChangedEvent } from "events/Event";
import "./Home.scss";

export type OutletContext = {
  storeNb: number;
  viewMode: VIEW_MODE;
  legendDisplay: LEGEND_MODE;
};

export const MAX_SNACKBAR = 50;
export const UNAVAILABLE_WAREHOUSE: Location = {
  name: "Aucun entrepôt disponible",
  storeNb: INVALID_INDEX,
};

type Props = {
  elementsPlan?: WarehouseInfos;
  statusPlan?: StatusResource;
};
export function HomePage({ elementsPlan, statusPlan }: Props): JSX.Element {
  const dispatch = useAppDispatch();

  const [warehouse, setWarehouse] = useState<Location>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(VIEW_MODE.NORMAL);
  const [legendDisplay, setLegend] = useState(LEGEND_MODE.REDUCED);
  const { isLoading: isLocationLoading, error: locationError } = useLocations();
  const warehouses = useAppSelector(selectLocations);

  const { isError: isResetTokenValidityError } =
    useResetTokenValidity(viewMode);

  useEffect(() => {
    if (isResetTokenValidityError) {
      dispatch(logout());
    }
  }, [isResetTokenValidityError]);
  useEffect(() => {
    if (isLocationLoading) {
      dispatch(addLoadingRequest("home"));
    } else {
      dispatch(removeLoadingRequest("home"));
    }
    return () => {
      dispatch(removeLoadingRequest("home"));
    };
  }, [isLocationLoading, dispatch]);

  useEffect(() => {
    if (isLocationLoading || warehouses.length === 0) return;

    let wantedWarehouseNb = -1;
    const urlWarehouse = getUrlParamOrLocalStorage(
      PARAM_KEY.WAREHOUSE,
      searchParams
    );

    if (urlWarehouse) {
      localStorage.setItem(PARAM_KEY.WAREHOUSE, urlWarehouse);
      wantedWarehouseNb = +urlWarehouse;
    }
    let warehouseToSet: Location | undefined;
    if (wantedWarehouseNb >= 0) {
      warehouseToSet = wantedWarehouseNb
        ? warehouses.find(w => w.storeNb === +wantedWarehouseNb)
        : undefined;
    }

    if (!warehouseToSet || wantedWarehouseNb < 0)
      warehouseToSet = warehouses[0];
    setWarehouse(warehouseToSet);

    const newURLParams = addNewURLQueryParams(
      warehouseToSet.storeNb.toString(),
      PARAM_KEY.WAREHOUSE,
      searchParams
    );
    setSearchParams(newURLParams);
  }, [warehouses, isLocationLoading, searchParams]);

  useEffect(() => {
    EntrepotChangedEvent.publish(warehouse?.storeNb ?? -1);
  }, [warehouse])

  useEffect(() => {
    if (searchParams) {
      const viewModeParam = getUrlParamOrLocalStorage(
        PARAM_KEY.FULLSCREEN,
        searchParams
      );
      const legendModeParam = getUrlParamOrLocalStorage(
        PARAM_KEY.LEGEND,
        searchParams
      );
      let newURLParams: URLSearchParams = searchParams;
      if (viewModeParam) {
        const enumViewMode =
          convertToEnumValue(VIEW_MODE, viewModeParam) ?? VIEW_MODE.NORMAL;
        localStorage.setItem(PARAM_KEY.FULLSCREEN, enumViewMode);
        setViewMode(enumViewMode);
        if (enumViewMode == VIEW_MODE.FULLSCREEN) {
          newURLParams = addNewURLQueryParams(
            enumViewMode,
            PARAM_KEY.FULLSCREEN,
            newURLParams
          );
        }
      }
      if (legendModeParam) {
        const enumLegendMode =
          convertToEnumValue(LEGEND_MODE, legendModeParam) ??
          LEGEND_MODE.REDUCED;
        localStorage.setItem(PARAM_KEY.LEGEND, enumLegendMode);
        setLegend(enumLegendMode);
        if (
          enumLegendMode == LEGEND_MODE.FULL ||
          enumLegendMode == LEGEND_MODE.NONE
        ) {
          newURLParams = addNewURLQueryParams(
            enumLegendMode,
            PARAM_KEY.LEGEND,
            newURLParams
          );
        }
      }
      setSearchParams(newURLParams);
    }
  }, [searchParams]);

  function handleWarehouseChange(newWarehouse: Location) {
    setWarehouse(newWarehouse);
    dispatch(resetGridEvent());
  }

  if (isLocationLoading) {
    return <p>Chargement....</p>;
  }

  if (locationError || !warehouse) {
    if (locationError && "status" in locationError) {
      if(locationError.status == UNAUTHORIZED_ERROR_CODE){
        dispatch(logout());
      }
      createErrorComponent(locationError);
    }
    return (
      <>
        <TanguayHeader />
        <div className="app-container">
          <SideBar />
          <div className="app-content-container">
            <WarehouseSelectionHeader
              warehouse={UNAVAILABLE_WAREHOUSE}
              onWarehouseChange={handleWarehouseChange}
            />
            <h1>{UNAVAILABLE_WAREHOUSE.name}</h1>
          </div>
        </div>
      </>
    );
  }
  return (
    <div className="sge">
      <SnackbarProvider
        maxSnack={MAX_SNACKBAR}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        style={{
          display: "flex",
          flexWrap: "nowrap",
          maxWidth: "50vw",
        }}>
        {viewMode == VIEW_MODE.NORMAL ? (
          <>
            <TanguayHeader />
            <div className="app-container">
              <SideBar />
              <div className="app-content-container">
                <WarehouseSelectionHeader
                  warehouse={warehouse}
                  onWarehouseChange={handleWarehouseChange}
                />
                <Outlet
                  context={
                    {
                      storeNb: warehouse.storeNb,
                      viewMode: viewMode,
                      legendDisplay: legendDisplay,
                    } satisfies OutletContext
                  }
                />
              </div>
            </div>
          </>
        ) : (
          <Outlet
            context={
              {
                storeNb: warehouse.storeNb,
                viewMode: viewMode,
                legendDisplay: legendDisplay,
              } satisfies OutletContext
            }
          />
        )}
      </SnackbarProvider>
    </div>
  );
}
